import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { ArrowLeft, Plus, Shield, Pencil, Trash2, Eye, EyeOff, Ban, Check, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import api from '../services/api';
import { toast } from 'sonner';

// Page labels in Czech
const PAGE_LABELS = {
  reports: 'Hlášení',
  sensory_hotovky: 'Senzorika Hotovky',
  sensory_krabicky: 'Senzorika Krabičky',
  autoclave: 'Autokláv',
  batch_search: 'Vyhledávání šarží',
  tasks: 'Úkoly',
  audits: 'Interní inspekce',
  production: 'Výroba',
  kitchen: 'Kuchyně',
  balirna: 'Balírna',
  handover_records: 'Záznamy předávky',
  admin: 'Administrace'
};

const DEFAULT_PERMISSIONS = {
  reports: 'view',
  sensory_hotovky: 'view',
  sensory_krabicky: 'view',
  autoclave: 'view',
  batch_search: 'view',
  tasks: 'none',
  audits: 'none',
  production: 'none',
  kitchen: 'none',
  balirna: 'none',
  handover_records: 'none',
  admin: 'none'
};

export const RolesAdminPage = () => {
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    page_permissions: { ...DEFAULT_PERMISSIONS }
  });

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await api.get('/roles');
      setRoles(response.data);
    } catch (error) {
      toast.error('Chyba při načítání rolí');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingRole(null);
    setFormData({
      name: '',
      description: '',
      page_permissions: { ...DEFAULT_PERMISSIONS }
    });
    setDialogOpen(true);
  };

  const openEditDialog = (role) => {
    setEditingRole(role);
    setFormData({
      name: role.name,
      description: role.description || '',
      page_permissions: { ...role.page_permissions }
    });
    setDialogOpen(true);
  };

  const handlePermissionChange = (pageKey, level) => {
    setFormData(prev => ({
      ...prev,
      page_permissions: {
        ...prev.page_permissions,
        [pageKey]: level
      }
    }));
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Zadejte název role');
      return;
    }

    try {
      if (editingRole) {
        await api.put(`/roles/${editingRole.id}`, formData);
        toast.success('Role aktualizována');
      } else {
        await api.post('/roles', formData);
        toast.success('Role vytvořena');
      }
      setDialogOpen(false);
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Chyba při ukládání role');
    }
  };

  const openDeleteDialog = (role) => {
    setRoleToDelete(role);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!roleToDelete) return;

    try {
      await api.delete(`/roles/${roleToDelete.id}`);
      toast.success('Role smazána');
      setDeleteDialogOpen(false);
      setRoleToDelete(null);
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Chyba při mazání role');
    }
  };

  // Count permissions summary
  const getPermissionSummary = (permissions) => {
    const editCount = Object.values(permissions).filter(v => v === 'edit').length;
    const viewCount = Object.values(permissions).filter(v => v === 'view').length;
    const deniedCount = Object.values(permissions).filter(v => v === 'denied').length;
    const noneCount = Object.values(permissions).filter(v => v === 'none').length;
    
    return { editCount, viewCount, deniedCount, noneCount };
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="text-slate-500">Načítání...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-8" data-testid="roles-admin-page">
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" onClick={() => navigate('/admin')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Zpět
          </Button>
        </div>

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Správa rolí</h1>
            <p className="text-lg text-slate-600">Definice rolí a jejich oprávnění ke stránkám</p>
          </div>
          <Button
            onClick={openCreateDialog}
            className="bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-600 hover:to-emerald-600"
            data-testid="create-role-button"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nová role
          </Button>
        </div>

        {/* Roles Grid */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {roles.map((role) => {
            const summary = getPermissionSummary(role.page_permissions);
            return (
              <div
                key={role.id}
                className={`bg-white rounded-xl border p-5 shadow-sm transition-all hover:shadow-md ${
                  role.is_admin ? 'border-purple-300 bg-purple-50/30' : 'border-cyan-200'
                }`}
                data-testid={`role-card-${role.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      role.is_admin 
                        ? 'bg-gradient-to-br from-purple-500 to-pink-500' 
                        : 'bg-gradient-to-br from-cyan-500 to-emerald-500'
                    }`}>
                      <Shield className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{role.name}</h3>
                      {role.is_system && (
                        <Badge variant="outline" className="text-xs bg-slate-100 text-slate-600 mt-1">
                          <Lock className="h-3 w-3 mr-1" />
                          Systémová
                        </Badge>
                      )}
                    </div>
                  </div>
                  {!role.is_system && (
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openEditDialog(role)}
                        className="h-8 w-8 p-0 text-slate-500 hover:text-cyan-600"
                        data-testid={`edit-role-${role.id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openDeleteDialog(role)}
                        className="h-8 w-8 p-0 text-slate-500 hover:text-red-600"
                        data-testid={`delete-role-${role.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                  {role.is_system && (
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openEditDialog(role)}
                        className="h-8 w-8 p-0 text-slate-500 hover:text-cyan-600"
                        data-testid={`edit-role-${role.id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>

                {role.description && (
                  <p className="text-sm text-slate-600 mb-3">{role.description}</p>
                )}

                <div className="flex flex-wrap gap-1">
                  {summary.editCount > 0 && (
                    <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
                      <Pencil className="h-3 w-3 mr-1" />
                      {summary.editCount} editace
                    </Badge>
                  )}
                  {summary.viewCount > 0 && (
                    <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                      <Eye className="h-3 w-3 mr-1" />
                      {summary.viewCount} prohlížení
                    </Badge>
                  )}
                  {summary.deniedCount > 0 && (
                    <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">
                      <Ban className="h-3 w-3 mr-1" />
                      {summary.deniedCount} zakázáno
                    </Badge>
                  )}
                  {summary.noneCount > 0 && (
                    <Badge variant="outline" className="text-xs bg-slate-50 text-slate-500 border-slate-200">
                      <EyeOff className="h-3 w-3 mr-1" />
                      {summary.noneCount} skryté
                    </Badge>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {roles.length === 0 && (
          <div className="text-center py-12 text-slate-400">
            <Shield className="h-12 w-12 mx-auto mb-3" />
            <p>Žádné role</p>
          </div>
        )}

        {/* Create/Edit Role Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="role-dialog">
            <DialogHeader>
              <DialogTitle className="text-xl">
                {editingRole ? `Upravit roli: ${editingRole.name}` : 'Nová role'}
              </DialogTitle>
              <DialogDescription>
                {editingRole?.is_system 
                  ? 'Upravujete systémovou roli. Systémové role nelze smazat, ale můžete změnit jejich oprávnění.'
                  : 'Nastavte název role a oprávnění k jednotlivým stránkám.'}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Name & Description */}
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label htmlFor="role-name">Název role *</Label>
                  <Input
                    id="role-name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="např. Vedoucí směny"
                    data-testid="role-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="role-description">Popis</Label>
                  <Input
                    id="role-description"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Krátký popis role"
                  />
                </div>
              </div>

              {/* Page Permissions Table */}
              <div>
                <Label className="mb-3 block">Oprávnění ke stránkám</Label>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-50 border-b">
                        <th className="text-left p-3 font-medium text-slate-700">Stránka</th>
                        <th className="text-center p-3 font-medium text-slate-400 w-20">
                          <div className="flex flex-col items-center">
                            <EyeOff className="h-4 w-4 mb-1" />
                            <span className="text-xs">Skryté</span>
                          </div>
                        </th>
                        <th className="text-center p-3 font-medium text-blue-600 w-20">
                          <div className="flex flex-col items-center">
                            <Eye className="h-4 w-4 mb-1" />
                            <span className="text-xs">Zobrazit</span>
                          </div>
                        </th>
                        <th className="text-center p-3 font-medium text-emerald-600 w-20">
                          <div className="flex flex-col items-center">
                            <Pencil className="h-4 w-4 mb-1" />
                            <span className="text-xs">Editovat</span>
                          </div>
                        </th>
                        <th className="text-center p-3 font-medium text-red-600 w-20">
                          <div className="flex flex-col items-center">
                            <Ban className="h-4 w-4 mb-1" />
                            <span className="text-xs">Zakázáno</span>
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(PAGE_LABELS).map(([pageKey, label]) => {
                        const currentLevel = formData.page_permissions[pageKey] || 'none';
                        return (
                          <tr key={pageKey} className="border-b last:border-b-0 hover:bg-slate-50">
                            <td className="p-3">
                              <span className="font-medium text-slate-800">{label}</span>
                            </td>
                            {['none', 'view', 'edit', 'denied'].map((level) => (
                              <td key={level} className="text-center p-3">
                                <button
                                  onClick={() => handlePermissionChange(pageKey, level)}
                                  className={`w-8 h-8 rounded-full flex items-center justify-center transition-all cursor-pointer ${
                                    currentLevel === level
                                      ? level === 'none' ? 'bg-slate-200 text-slate-600'
                                        : level === 'view' ? 'bg-blue-100 text-blue-600 ring-2 ring-blue-500'
                                        : level === 'edit' ? 'bg-emerald-100 text-emerald-600 ring-2 ring-emerald-500'
                                        : 'bg-red-100 text-red-600 ring-2 ring-red-500'
                                      : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                                  }`}
                                  data-testid={`perm-${pageKey}-${level}`}
                                >
                                  {currentLevel === level && <Check className="h-4 w-4" />}
                                </button>
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Legend */}
              <div className="p-3 bg-slate-50 rounded-lg text-sm">
                <div className="font-medium text-slate-700 mb-2">Vysvětlivky:</div>
                <div className="grid grid-cols-2 gap-2 text-slate-600">
                  <div className="flex items-center gap-2">
                    <EyeOff className="h-4 w-4 text-slate-400" />
                    <span><strong>Skryté</strong> - stránka není v menu</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Eye className="h-4 w-4 text-blue-600" />
                    <span><strong>Zobrazit</strong> - může prohlížet</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Pencil className="h-4 w-4 text-emerald-600" />
                    <span><strong>Editovat</strong> - plný přístup</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Ban className="h-4 w-4 text-red-600" />
                    <span><strong>Zakázáno</strong> - vidí, ale "Nemáte oprávnění"</span>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Zrušit
              </Button>
              <Button
                onClick={handleSave}
                className="bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-600 hover:to-emerald-600"
                data-testid="save-role-button"
              >
                {editingRole ? 'Uložit změny' : 'Vytvořit roli'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent data-testid="delete-role-dialog">
            <DialogHeader>
              <DialogTitle>Smazat roli</DialogTitle>
              <DialogDescription>
                Opravdu chcete smazat roli <strong>{roleToDelete?.name}</strong>?
                Tuto akci nelze vrátit zpět.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                Zrušit
              </Button>
              <Button
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-delete-role-button"
              >
                Smazat roli
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default RolesAdminPage;
